import Phaser from 'phaser';

// Map preview: a small, semi-transparent micrograph of the whole level
// (walls light, floor dark) with your dot, the camera's view rectangle and
// dots for the other entities the game chooses to show. The texture is one
// pixel per tile, built once per level version; per frame only the dot, the
// rectangle and a few circles are drawn. See docs/map-preview.md.
export interface PreviewLevel {
  name: string;
  cols: number;
  rows: number;
  tileSize: number;
  solid: Uint8Array; // cols * rows, 1 = wall
  opaque?: Uint8Array; // 1 = blocks shots too; solid but not opaque is water
}

// What the preview reads per entity each frame; a game's World satisfies it.
export interface PreviewEntities {
  max: number;
  alive: Uint8Array;
  x: Float32Array;
  y: Float32Array;
}

export interface PreviewOptions {
  maxW?: number; // box the level fits in (default 100 x 75)
  maxH?: number;
  margin?: number; // from the top-left corner (default 8)
  alpha?: number; // of the map (default 0.45)
  wallRgb?: [number, number, number];
  floorRgb?: [number, number, number];
  waterRgb?: [number, number, number];
}

// Colour for an entity's dot, or 0 to not draw it (hidden, dead, disguised).
export type DotColorFn = (id: number) => number;

export class MapPreview {
  private scale = 1; // preview px per world px
  private readonly origin = { x: 0, y: 0 };
  readonly size = { w: 0, h: 0 };
  private image: Phaser.GameObjects.Image | null = null;
  private frame: Phaser.GameObjects.Graphics | null = null;
  private view: Phaser.GameObjects.Graphics | null = null;
  private dot: Phaser.GameObjects.Arc | null = null;
  private readonly opts: Required<PreviewOptions>;

  constructor(
    private readonly scene: Phaser.Scene,
    opts: PreviewOptions = {},
  ) {
    this.opts = {
      maxW: opts.maxW ?? 100,
      maxH: opts.maxH ?? 75,
      margin: opts.margin ?? 8,
      alpha: opts.alpha ?? 0.45,
      wallRgb: opts.wallRgb ?? [220, 220, 200],
      floorRgb: opts.floorRgb ?? [40, 60, 40],
      waterRgb: opts.waterRgb ?? [60, 110, 160],
    };
  }

  // (Re)build for a level; safe to call again after a live level swap.
  build(level: PreviewLevel): void {
    this.image?.destroy();
    this.frame?.destroy();
    this.view?.destroy();
    this.dot?.destroy();

    const key = `minimap:${level.name}`;
    if (this.scene.textures.exists(key)) this.scene.textures.remove(key);
    const canvas = this.scene.textures.createCanvas(key, level.cols, level.rows)!;
    const ctx = canvas.getContext();
    const img = ctx.createImageData(level.cols, level.rows);
    const [wr, wg, wb] = this.opts.wallRgb;
    const [fr, fg, fb] = this.opts.floorRgb;
    const [ar, ag, ab] = this.opts.waterRgb;
    for (let i = 0; i < level.solid.length; i++) {
      const solid = level.solid[i] === 1;
      const water = solid && level.opaque?.[i] === 0;
      img.data[i * 4] = water ? ar : solid ? wr : fr;
      img.data[i * 4 + 1] = water ? ag : solid ? wg : fg;
      img.data[i * 4 + 2] = water ? ab : solid ? wb : fb;
      img.data[i * 4 + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    canvas.refresh();

    const scaleTiles = Math.min(this.opts.maxW / level.cols, this.opts.maxH / level.rows);
    this.scale = scaleTiles / level.tileSize;
    const mapW = Math.round(level.cols * scaleTiles);
    const mapH = Math.round(level.rows * scaleTiles);
    this.size.w = mapW;
    this.size.h = mapH;
    this.origin.x = this.opts.margin;
    this.origin.y = this.opts.margin;

    const map = this.scene.add.image(this.origin.x, this.origin.y, key).setOrigin(0, 0).setAlpha(this.opts.alpha);
    map.setDisplaySize(mapW, mapH);
    map.texture.setFilter(Phaser.Textures.FilterMode.NEAREST);
    this.image = map;

    this.frame = this.scene.add.graphics();
    this.frame.lineStyle(1, 0xffffff, 0.25).strokeRect(this.origin.x - 0.5, this.origin.y - 0.5, mapW + 1, mapH + 1);
    this.view = this.scene.add.graphics();
    this.dot = this.scene.add.circle(0, 0, 3, 0x7ee07a).setStrokeStyle(1, 0x000000, 0.6);
  }

  // Per frame. `dotColor(id)` decides which other entities are marked.
  update(myId: number, ents: PreviewEntities, camera: Phaser.Cameras.Scene2D.Camera, dotColor: DotColorFn): void {
    const g = this.view;
    const dot = this.dot;
    if (!g || !dot) return;
    g.clear();
    if (myId < 0 || ents.alive[myId] === 0) {
      dot.setVisible(false);
      return;
    }
    const s = this.scale;
    const ox = this.origin.x;
    const oy = this.origin.y;
    dot.setVisible(true).setPosition(ox + ents.x[myId]! * s, oy + ents.y[myId]! * s);
    const v = camera.worldView;
    g.lineStyle(1, 0xffffff, 0.3).strokeRect(ox + v.x * s, oy + v.y * s, v.width * s, v.height * s);
    for (let id = 0; id < ents.max; id++) {
      if (id === myId || ents.alive[id] === 0) continue;
      const color = dotColor(id);
      if (color === 0) continue;
      g.fillStyle(color, 0.95).fillCircle(ox + ents.x[id]! * s, oy + ents.y[id]! * s, 2.5);
    }
  }
}
